import getWindow from 'get-window';

import {
    Block,
    BlockRange,
    type Change,
    type Fragment,
    Inline,
    isBlockRangeSelection,
    isTextRangeSelection,
    type NodeType,
    serialize,
    type Text,
    TextRange,
    type Value,
} from '@gitbook/slate';
import { IS_IOS } from '@gitbook/slate-dev-environment';
import Hotkeys from '@gitbook/slate-hotkeys';
import { Logger } from '@gitbook/util-debug';

import { TRIPLE_CLICK } from '../constants';
import type { EditorContainer } from '../interfaces';
import {
    decorateClipboardEvent,
    encodeToBase64,
    findDOMPoint,
    findNode,
    findRange,
    findDOMNode as findSlateDOMNode,
    getEventRange,
    getEventTransfer,
    removeCutContent,
    setEventTransfer,
} from '../utils';
import { isBlockSelectionPrevented } from './block-selection';
import type { Plugin } from './plugin';

const logger = Logger('slate:react:after');

/*
 * The after plugin.
 */
function AfterPlugin(): Plugin {
    let isDraggingInternally: boolean | null = null;

    let isTogglingSelection = false;

    /**
     * Changes that are deferred from `onBeforeInput` to `onInput`
     * to let the browser updates the DOM first before reconcilliating with rendering.
     */
    let deferredChanges: Array<(change: Change) => void> = [];

    // Correct any browser inconsistencies in `beforeinput` events.
    function onBeforeInput(event: InputEvent, change: Change, editor: EditorContainer) {
        logger.debug('onBeforeInput', { event });

        const { value } = change;
        const isBlockSelection = isBlockRangeSelection(value.selection);
        // During block selection we don't want to handle any input events
        // because the block and not text content is selected.
        if (isBlockSelection) {
            event.preventDefault();
            return;
        }

        switch (event.inputType) {
            case 'deleteByComposition':
            case 'deleteByCut':
            case 'deleteByDrag': {
                event.preventDefault();
                change.delete();
                break;
            }

            case 'deleteContent':
            case 'deleteContentForward': {
                event.preventDefault();
                change.deleteForward();
                break;
            }

            case 'deleteContentBackward': {
                event.preventDefault();
                change.deleteBackward();
                break;
            }

            case 'deleteEntireSoftLine':
            case 'deleteHardLineBackward':
            case 'deleteSoftLineBackward':
            case 'deleteHardLineForward':
            case 'deleteSoftLineForward':
            case 'deleteWordBackward':
            case 'deleteWordForward': {
                // TODO: implement
                break;
            }

            case 'insertLineBreak': // intentional fallthru
            case 'insertParagraph': {
                event.preventDefault();

                if (change.value.isInVoid) {
                    change.collapseToStartOfNextText();
                } else {
                    change.splitBlock();
                }

                break;
            }

            case 'insertCompositionText':
            case 'deleteCompositionText': {
                // Composition change types occur while a user is composing text and can't be
                // cancelled. Let them through and wait for the composition to end.
                break;
            }

            case 'insertFromComposition':
            case 'insertFromDrop':
            case 'insertFromPaste':
            case 'insertFromYank':
            case 'insertReplacementText':
            case 'insertText': {
                const native = shouldUseNativeBeforeInput(event, editor, change.value);

                if (!native) {
                    event.preventDefault();
                }

                if (event.inputType === 'insertFromComposition') {
                    // COMPAT: in Safari, `compositionend` is dispatched after the
                    // `beforeinput` for "insertFromComposition". But if we wait for it
                    // then we will abort because we're still composing and the selection
                    // won't be updated properly.
                    // https://www.w3.org/TR/input-events-2/
                    editor.isComposing = false;
                }

                // `data` should have the text for the `insertText` input type and
                // `dataTransfer` should have the text for the `insertReplacementText`
                // input type, but Safari uses `insertText` for spell check replacements
                // and sets `data` to `null`.
                const text =
                    event.data == null && event.dataTransfer
                        ? event.dataTransfer.getData('text/plain')
                        : event.data;

                if (text == null) {
                    return;
                }

                const changeInsertText = (change: Change) => {
                    change.insertText(text);

                    // If the text was successfully inserted, and the selection had marks
                    // on it, unset the selection's marks.
                    if (
                        isTextRangeSelection(editor.value.selection) &&
                        editor.value.selection.marks &&
                        editor.value.document !== change.value.document
                    ) {
                        change.select({ marks: null });
                    }
                };

                if (native) {
                    // Let the browser update the DOM first before reconcilliating with rendering.
                    // The changes will be applied in `onInput`.
                    deferredChanges.push(changeInsertText);
                } else {
                    changeInsertText(change);
                }

                break;
            }
        }
    }

    function onBlur(event: Event, change: Change) {
        logger.debug('onBlur', { event });
        change.blur();
    }

    function onClick(event: MouseEvent, change: Change, editor: EditorContainer): boolean | void {
        if (editor.readOnly) {
            return true;
        }

        const { value } = change;
        const { document } = value;
        const node = findNode(event.target as HTMLElement, value);

        if (!node) {
            return;
        }

        if (
            node.object !== 'fragment' &&
            node.object !== 'text' &&
            !document.isInFragment(node.key) &&
            (node.isVoid || document.hasVoidParent(node.key))
        ) {
            // COMPAT: In Chrome & Safari, selections that are at the zero offset of
            // an inline node will be automatically replaced to be at the last offset
            // of a previous inline node, which screws us up, so we always want to set
            // it to the end of the node. (2016/11/29)
            change.focus().collapseToEndOf(node);
        }

        // Fix triple click bug in Chrome where the native selection collapses to beginning of node rather
        // than selecting the whole node.
        //
        // Also makes the selection more consistent in Safari where triple click would not select the whole
        // paragraph where there were inline nodes or remote cursors.
        if (event.detail >= TRIPLE_CLICK) {
            if (node.isText() || node.isInline()) {
                change.focus().collapseToStartOfBlock().extendToEndOfBlock();
            } else if (node.isBlock()) {
                const text = node.getFirstText();
                if (text) {
                    change.focus().collapseToStartOfBlock().extendToEndOfBlock();
                }
            }
        }

        logger.debug('onClick', { event });
    }

    function onMouseDown(
        event: MouseEvent,
        change: Change,
        editor: EditorContainer
    ): boolean | void {
        // If the editor is read only, we don't do anything,
        // and we stop the event from going any further.
        if (editor.readOnly) {
            return true;
        }

        // If the user is holding shift, they are trying to extend the selection
        // we want to allow to select multiple blocks.
        if (event.shiftKey) {
            // If the block selection is prevented, we let the event go through.
            if (isBlockSelectionPrevented(event)) {
                return;
            }

            const node = findNode(event.target as HTMLElement, change.value);

            // If we didn't find a node, we can't do anything.
            if (!node) {
                return;
            }

            const { document, selection } = change.value;
            const block = node.object === 'block' ? node : document.getClosestBlock(node.key);

            // If we didn't find a block, we can't do anything.
            if (!block) {
                return;
            }

            // If we are already in a block selection, we want to allow the user to extend the selection.
            if (isBlockRangeSelection(selection)) {
                event.preventDefault();
                change.select(selection.moveFocusTo(block.key));
                return;
            }

            // If the user is trying to extend the selection but the current selection is not in the block
            // we create a new block selection starting from the current block.
            if (!selection.hasEdgeIn(block)) {
                event.preventDefault();
                change.selectBlock(
                    BlockRange.fromTextRange(selection, document).moveFocusTo(block.key)
                );
                return;
            }
        }
    }

    function onCopy(event: ClipboardEvent, change: Change) {
        logger.debug('onCopy', { event });
        decorateClipboardEvent(event, change.value);
    }

    function onCut(event: ClipboardEvent, change: Change, editor: EditorContainer) {
        logger.debug('onCut', { event });

        const { value } = change;

        decorateClipboardEvent(event, value);
        removeCutContent(event, value, editor);
    }

    function onDragEnd(event: Event) {
        logger.debug('onDragEnd', { event });

        isDraggingInternally = null;
    }

    function onDragOver(event: Event) {
        logger.debug('onDragOver', { event });
    }

    function onDragStart(event: DragEvent, { value }: Change) {
        logger.debug('onDragStart', { event });

        isDraggingInternally = true;

        // @ts-expect-error
        const node = findNode(event.target, value);
        // @ts-expect-error
        const isVoid = node && (node.isVoid || value.document.hasVoidParent(node.key));

        if (isVoid) {
            // @ts-expect-error
            const encoded = encodeToBase64(serialize(node, { preserveKeys: true }));
            setEventTransfer(event, 'node', encoded);
        } else {
            const encoded = encodeToBase64(serialize(value.fragment, { preserveKeys: true }));
            setEventTransfer(event, 'fragment', encoded);
        }
    }

    function onDrop(event: DragEvent, change: Change) {
        logger.debug('onDrop', { event });

        if (event.defaultPrevented) {
            return;
        }

        const { value } = change;
        const { document, selection } = value;
        const window = getWindow(event.target);
        let target = getEventRange(event, value);
        if (!target) {
            return;
        }

        const transfer = getEventTransfer(event);
        const { type, fragment, node, text } = transfer;

        change.focus();

        // If the drag is internal and the target is after the selection, it
        // needs to account for the selection's content being deleted.
        if (
            isDraggingInternally &&
            // @ts-expect-error
            selection.endKey === target.endKey &&
            // @ts-expect-error
            selection.endOffset < target.endOffset
        ) {
            target = target.move(
                // @ts-expect-error
                selection.startKey === selection.endKey
                    ? // @ts-expect-error
                      selection.startOffset - selection.endOffset
                    : // @ts-expect-error
                      -1 * selection.endOffset
            );
        }

        if (isDraggingInternally) {
            change.delete();
        }

        change.select(target);

        if (type === 'text' || type === 'html') {
            const { anchorKey } = target;
            // @ts-expect-error
            let hasVoidParent = document.hasVoidParent(anchorKey);

            if (hasVoidParent) {
                // @ts-expect-error
                let n = document.getNode(anchorKey);

                while (hasVoidParent) {
                    // @ts-expect-error
                    n = document.getNextText(n.key);
                    if (!n) {
                        break;
                    }
                    hasVoidParent = document.hasVoidParent(n.key);
                }

                if (n) {
                    change.collapseToStartOf(n);
                }
            }

            if (text) {
                text.split('\n').forEach((line, i) => {
                    if (i > 0) {
                        change.splitBlock();
                    }
                    change.insertText(line);
                });
            }
        }

        if (type === 'fragment') {
            // @ts-expect-error
            change.insertDocument(fragment);
        }

        if (type === 'node' && Block.isBlock(node)) {
            change.insertBlock(node.regenerateKey()).removeNodeByKey(node.key);
        }

        if (type === 'node' && Inline.isInline(node)) {
            change.insertInline(node.regenerateKey()).removeNodeByKey(node.key);
        }

        // COMPAT: React's onSelect event breaks after an onDrop event
        // has fired in a node: https://github.com/facebook/react/issues/11379.
        // Until this is fixed in React, we dispatch a mouseup event on that
        // DOM node, since that will make it go back to normal.
        // @ts-expect-error
        const focusNode = document.getNode(target.focusKey);
        // @ts-expect-error
        const el = findSlateDOMNode(focusNode, window);
        if (!el) {
            return;
        }

        el.dispatchEvent(
            new MouseEvent('mouseup', {
                view: window,
                bubbles: true,
                cancelable: true,
            })
        );
    }

    function onInput(event: Event, change: Change) {
        logger.debug('onInput', { event });

        // Flush native operations, as native events will have propogated
        // and we can correctly compare DOM text values in components
        // to stop rendering, so that browser functions like autocorrect
        // and spellcheck work as expected.
        deferredChanges.forEach((fn) => {
            fn(change);
        });

        deferredChanges = [];
    }

    function onKeyDown(event: KeyboardEvent, change: Change) {
        logger.debug('onKeyDown', { event });

        const { value } = change;
        const isBlockSelection = isBlockRangeSelection(value.selection);

        if (Hotkeys.isExitBlockRange(event)) {
            if (isBlockSelection) {
                return change
                    .selectText(TextRange.fromBlockRange(value.selection, value.document))
                    .collapseLineForward();
            }
        }

        // COMPAT: In iOS, some of these hotkeys are handled in the
        // `onNativeBeforeInput` handler of the `<Content>` component in order to
        // preserve native autocorrect behavior, so they shouldn't be handled here.
        if (Hotkeys.isSplitBlock(event) && !IS_IOS) {
            if (isBlockSelection) {
                return;
            }
            return value.isInVoid ? change.collapseToStartOfNextText() : change.splitBlock();
        }

        if (Hotkeys.isDeleteCharBackward(event) && !IS_IOS) {
            return isBlockSelection
                ? change.deleteBlockRangeBackward()
                : change.deleteCharBackward();
        }

        if (Hotkeys.isDeleteCharForward(event) && !IS_IOS) {
            return isBlockSelection ? change.deleteBlockRangeForward() : change.deleteCharForward();
        }

        if (Hotkeys.isDeleteLineBackward(event)) {
            return isBlockSelection
                ? change.deleteBlockRangeBackward()
                : change.deleteLineBackward();
        }

        if (Hotkeys.isDeleteLineForward(event)) {
            return isBlockSelection ? change.deleteBlockRangeForward() : change.deleteLineForward();
        }

        if (Hotkeys.isDeleteWordBackward(event)) {
            return isBlockSelection
                ? change.deleteBlockRangeBackward()
                : change.deleteWordBackward();
        }

        if (Hotkeys.isDeleteWordForward(event)) {
            return isBlockSelection ? change.deleteBlockRangeForward() : change.deleteWordForward();
        }

        if (Hotkeys.isDuplicate(event)) {
            event.preventDefault();
            return change.duplicateBlocks();
        }

        if (Hotkeys.isRedo(event)) {
            return change.redo();
        }

        if (Hotkeys.isUndo(event)) {
            return change.undo();
        }

        // COMPAT: Certain browsers don't handle the selection updates properly. In
        // Chrome, the selection isn't properly extended. And in Firefox, the
        // selection isn't properly collapsed. (2017/10/17)
        if (Hotkeys.isCollapseLineBackward(event)) {
            event.preventDefault();
            return isBlockSelection
                ? change.moveBlockRangeBackward()
                : change.collapseLineBackward();
        }

        if (Hotkeys.isCollapseLineForward(event)) {
            event.preventDefault();
            return isBlockSelection ? change.moveBlockRangeForward() : change.collapseLineForward();
        }

        if (Hotkeys.isExtendLineBackward(event)) {
            event.preventDefault();
            return isBlockSelection
                ? change.resizeBlockRangeBackward()
                : change.extendLineBackward();
        }

        if (Hotkeys.isExtendLineForward(event)) {
            event.preventDefault();
            return isBlockSelection ? change.resizeBlockRangeForward() : change.extendLineForward();
        }

        // COMPAT: If a void node is selected, or a zero-width text node adjacent to
        // an inline is selected, we need to handle these hotkeys manually because
        // browsers won't know what to do.
        if (Hotkeys.isCollapseCharBackward(event)) {
            if (isBlockSelection) {
                event.preventDefault();
                return change.moveBlockRangeToAncestor();
            }

            const { document, isInVoid, previousText, startText } = value;
            const isPreviousInVoid = previousText && document.hasVoidParent(previousText.key);
            if (
                isInVoid ||
                startText?.text === '' ||
                (startText?.text.length === 0 && isPreviousInVoid)
            ) {
                event.preventDefault();
                return change.collapseCharBackward();
            }
        }

        // COMPAT: If a void node is selected, or a zero-width text node adjacent to
        // an inline is selected, we need to handle these hotkeys manually because
        // browsers won't know what to do.
        if (Hotkeys.isCollapseCharForward(event)) {
            if (isBlockSelection) {
                event.preventDefault();
                return change.moveBlockRangeToDescendant();
            }

            const { document, isInVoid, nextText, endOffset, endText } = value;
            const isNextInVoid = nextText && document.hasVoidParent(nextText.key);

            if (
                isInVoid ||
                endText?.text === '' ||
                (endOffset === endText?.text.length && isNextInVoid)
            ) {
                event.preventDefault();
                return change.collapseCharForward();
            }
        }

        if (Hotkeys.isExtendCharBackward(event)) {
            if (isBlockSelection) {
                event.preventDefault();
                return change.resizeBlockRangeBackward();
            }

            const { document, isInVoid, previousText, startText } = value;
            const isPreviousInVoid = previousText && document.hasVoidParent(previousText.key);

            // @ts-expect-error
            if (isInVoid || isPreviousInVoid || startText.text === '') {
                event.preventDefault();
                return change.extendCharBackward();
            }
        }

        if (Hotkeys.isExtendCharForward(event)) {
            if (isBlockSelection) {
                event.preventDefault();
                return change.resizeBlockRangeForward();
            }

            const { document, isInVoid, nextText, startText } = value;
            const isNextInVoid = nextText && document.hasVoidParent(nextText.key);

            // @ts-expect-error
            if (isInVoid || isNextInVoid || startText.text === '') {
                event.preventDefault();
                return change.extendCharForward();
            }
        }

        // Select only current fragment or top document when pressing Command+A
        if (Hotkeys.isSelectAll(event)) {
            if (isBlockSelection) {
                event.preventDefault();
                return change.selectAllBlockRange();
            }
            const fragment = value.document.getClosest(
                // @ts-expect-error
                value.startKey,
                (node: NodeType) => node.object === 'fragment'
            ) as Fragment | undefined;

            event.preventDefault();

            if (fragment) {
                return change.collapseToStartOf(fragment).extendToEndOf(fragment);
            }
            return change.collapseToStartOf(value.document).extendToEndOf(value.document);
        }

        // Swap from BlockRange to TextRange and vice versa
        // only do this when editor has a focused selection
        if (
            change.value.selection.isFocused &&
            !isTogglingSelection &&
            Hotkeys.isEscapeSelection(event)
        ) {
            // to stop really fast flickering between text and block
            // selections when a key is held down
            // we only want to toggle once per keydown
            // and then allow again after keyup
            isTogglingSelection = true;
            // we also don't want to prevent default as escape key is used to close modals and palettes
            return change.switchSelection();
        }

        /**
         * Key commands specific to BlockRange selection
         */
        if (isBlockSelection) {
            if (Hotkeys.isMoveUp(event)) {
                event.preventDefault();
                return change.moveBlockRangeBackward();
            }

            if (Hotkeys.isMoveDown(event)) {
                event.preventDefault();
                return change.moveBlockRangeForward();
            }

            if (Hotkeys.isExtendBlockBackward(event)) {
                event.preventDefault();
                return change.resizeBlockRangeBackward();
            }

            if (Hotkeys.isExtendBlockForward(event)) {
                event.preventDefault();
                return change.resizeBlockRangeForward();
            }
        }
    }

    function onKeyUp(event: KeyboardEvent) {
        logger.debug('onKeyUp', { event });
        if (Hotkeys.isEscapeSelection(event)) {
            // reset boolean to false on key up so we know
            // we can switch selection again
            isTogglingSelection = false;
        }
    }

    function onPaste(event: ClipboardEvent, change: Change) {
        logger.debug('onPaste', { event });

        const { type, fragment, text } = getEventTransfer(event);

        if (type === 'fragment') {
            // @ts-expect-error
            change.insertDocument(fragment);
        }

        // Basic handler for text/html, not used since it's being overriden by the plugin in the app
        if (type === 'text' || type === 'html') {
            if (!text) {
                return;
            }

            // @ts-expect-error
            if (change.value.startBlock.isVoid) {
                return;
            }

            change.insertText(text);
        }
    }

    function onSelect(event: Event, change: Change) {
        const window = getWindow(event.target);
        const { value } = change;
        const { document } = value;
        const native = window.getSelection();
        // If there are no ranges, the editor was blurred natively unless
        // we have moved to a block range selection.
        if (!native.rangeCount) {
            if (isTextRangeSelection(value.selection)) {
                change.blur();
            }
            return;
        }

        // Otherwise, determine the Slate selection from the native one.
        let range = findRange(native, value);
        if (!range) {
            return;
        }

        range = range.normalize(document).normalizeSelection(document);

        // Preserve active marks from the current selection.
        // They will be cleared by `change.select` if the selection actually moved.
        // @ts-expect-error
        range = range.set('marks', value.selection.marks);

        // Preserve selection data from the current selection.
        if (value.selection.data) {
            range = range.set('data', value.selection.data);
        }

        change.select(range);
    }

    /*
     * Render node.
     */

    function renderNode(props) {
        const { attributes, children, node } = props;
        if (node.object !== 'block' && node.object !== 'inline') {
            return;
        }
        const Tag = node.object === 'block' ? 'div' : 'span';
        const style = { position: 'relative' };
        return (
            <Tag {...attributes} style={style}>
                {children}
            </Tag>
        );
    }

    return {
        onBeforeInput,
        onBlur,
        onMouseDown,
        onClick,
        onCopy,
        onCut,
        onDragEnd,
        onDragOver,
        onDragStart,
        onDrop,
        onInput,
        onKeyDown,
        onKeyUp,
        onPaste,
        onSelect,
        renderNode,
    };
}

/**
 * Check if the selection's anchor is at the boundary of a decorated leaf.
 */
function isSelectionAtDecoratedLeafBoundary(editor: EditorContainer) {
    const { selection, document } = editor.value;

    // @ts-expect-error
    if (!selection.anchorKey) {
        return false;
    }

    // @ts-expect-error
    const ancestors = document.getAncestors(selection.anchorKey);
    if (
        ancestors &&
        ancestors.size > 0 &&
        ancestors.some((ancestors) => {
            const decorations = editor.stack.getDecorations(ancestors);
            return decorations.some(
                (decoration) =>
                    // @ts-expect-error
                    decoration.anchorKey === selection.anchorKey &&
                    // @ts-expect-error
                    (decoration.anchorOffset === selection.anchorOffset ||
                        // @ts-expect-error
                        decoration.focusOffset === selection.anchorOffset)
            );
        })
    ) {
        return true;
    }

    return false;
}

/**
 * Native-insertion during a beforeinput means that we let the browser makes the change
 * to the DOM, and then we reconcile the DOM change into a Slate change in the `onInput`.
 */
function shouldUseNativeBeforeInput(event: InputEvent, editor: EditorContainer, value: Value) {
    let native = false;
    const { selection, marks } = editor.value;

    if (selection !== value.selection) {
        // If we moved the selection in the onBeforeInput handler, we need to
        // handle the operation ourselves.
        return false;
    }

    if (
        event.inputType === 'insertText' &&
        selection.isSet &&
        selection.isCollapsed &&
        // Only use native character insertion for single characters a-z or space for now.
        // Long-press events (hold a + press 4 = ä) to choose a special character otherwise
        // causes duplicate inserts.
        event.data &&
        event.data.length === 1 &&
        /[a-z ]/i.test(event.data) &&
        // Chrome has issues correctly editing the start of nodes: https://bugs.chromium.org/p/chromium/issues/detail?id=1249405
        // When there is an inline element, e.g. a link, and you select
        // right after it (the start of the next node).
        // @ts-expect-error
        selection.startOffset !== 0
    ) {
        native = true;

        // Skip native if there are marks, as
        // `insertText` will insert a node, not just text.
        if (marks.size) {
            native = false;
        }

        // Skip native if inserting right after a mark as the browser will not know where to insert (in or outside the leaf).
        const { anchorText } = editor.value;
        // @ts-expect-error
        if (anchorText?.isLeafBoundary(selection.anchorOffset)) {
            native = false;
        }

        // Skip native if inserting at the boundary of a decorated leaf as the browser will not know in which leaf to insert
        if (native && isSelectionAtDecoratedLeafBoundary(editor)) {
            native = false;
        }

        // Chrome also has issues correctly editing the end of anchor elements: https://bugs.chromium.org/p/chromium/issues/detail?id=1259100
        // Therefore we don't allow native events to insert text at the end of anchor nodes.
        const editorDom = editor.elementRef?.current;
        // @ts-expect-error
        const anchorPoint = selection.anchorKey
            ? // @ts-expect-error
              findDOMPoint(selection.anchorKey, selection.anchorOffset)
            : null;
        const anchorNode = anchorPoint?.node.parentElement?.closest('a');

        if (editorDom && anchorPoint && native && anchorNode && editorDom.contains(anchorNode)) {
            // Find the last text node inside the anchor.
            const lastText = getWindow(editorDom)
                .document.createTreeWalker(anchorNode, NodeFilter.SHOW_TEXT)
                .lastChild() as Text | null;

            if (
                // @ts-expect-error
                lastText === anchorPoint.node &&
                // @ts-expect-error
                lastText.textContent?.length === anchorPoint.offset
            ) {
                native = false;
            }
        }

        // Chrome has issues with the presence of tab characters inside elements with whiteSpace = 'pre'
        // causing abnormal insert behavior: https://bugs.chromium.org/p/chromium/issues/detail?id=1219139
        if (
            anchorPoint &&
            native &&
            anchorPoint.node.parentElement &&
            // Safety condition for Safari to avoid error about parentElement not being an Element.
            anchorPoint.node.parentElement instanceof HTMLElement &&
            window?.getComputedStyle(anchorPoint.node.parentElement)?.whiteSpace === 'pre'
        ) {
            // @ts-expect-error
            const parentBlock = selection.anchorKey
                ? // @ts-expect-error
                  editor.value.document.getClosestBlock(selection.anchorKey)
                : null;
            if (parentBlock?.text.includes('\t')) {
                native = false;
            }
        }
    }

    return native;
}

export default AfterPlugin;
